import { useState } from "react";
import { useApiService } from "../../services/apiService";
import { conversationPath } from "./assistantTypes";
import { AddToCollections } from "../product-collections/AddToCollections";
import {
  openProductDetail,
  PRODUCT_NAVIGATION_EVENT,
} from "../product-detail/navigation";
import { Button } from "../ui/button";
import { Package } from "lucide-react";
import {
  conversationUrl,
  safeImage,
  widgetSchema,
  type Product,
} from "./assistantTypes";

function Evidence({ sourceId }: { sourceId?: string | null }) {
  return sourceId ? (
    <details className="assistant-evidence">
      <summary>Evidence</summary>
      <span>{sourceId}</span>
    </details>
  ) : null;
}

export function AssistantWidget({
  data,
  conversationId,
  selected,
  onSelect,
  onPrompt,
  disabled,
}: {
  data: unknown;
  conversationId: string;
  selected: number[];
  onSelect: (product: Product) => void;
  onPrompt: (text: string) => void;
  disabled: boolean;
}) {
  const api = useApiService();
  const source = JSON.stringify(data);
  const [localResult, setLocalResult] = useState<{ source: string | undefined; data: unknown } | null>(null);
  const pageData = localResult && localResult.source === source ? localResult.data : data;
  const setPageData = (next: unknown) => setLocalResult({ source, data: next });
  const [pageBusy, setPageBusy] = useState(false);
  const [pageError, setPageError] = useState("");
  const result = widgetSchema.safeParse(pageData);
  async function loadPage(resultSetId: string, page: number) {
    setPageBusy(true); setPageError("");
    try {
      const next = await api.get(`${conversationPath}/${conversationId}/results/${resultSetId}?page=${page}`);
      if (!widgetSchema.safeParse(next).success) throw new Error("Invalid result page.");
      setPageData(next);
    } catch { setPageError("This page could not be loaded or the results changed. Retry or refresh the search."); }
    finally { setPageBusy(false); }
  }
  const evidence = (facts: { entityId: number; perspective: string; attribute: string; value: string }[]) => (
    <details className="assistant-evidence"><summary>Supporting recorded facts</summary><ul>{facts.map((fact, i) => <li key={i}><a href={`#fact-${fact.entityId}-${fact.perspective}-${encodeURIComponent(fact.attribute)}`}>Product {fact.entityId} · {fact.attribute}</a>: {fact.value}</li>)}</ul></details>
  );
  if (!result.success)
    return (
      <p role="alert">
        This product widget could not be displayed. Try product search.
      </p>
    );
  const widget = result.data;
  const productCards = (products: Product[]) => (
    <div className="assistant-products">
      {products.map((product) => (
        <article className="assistant-product" key={product.entityId}>
          <div className="assistant-product-image">
            <Package size={32} aria-hidden="true" />
            {safeImage(product.imageUrl) && (
              <img
                loading="lazy"
                src={safeImage(product.imageUrl)}
                alt=""
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            )}
          </div>
          <div className="assistant-product-info">
            <strong>{product.name}</strong>
            <span>
              {[product.itemNumber, product.brand, product.category]
                .filter(Boolean)
                .join(" · ")}
            </span>
            {product.description && (
              <p className="assistant-product-description">
                {product.description}
              </p>
            )}
            {widget.component !== "ProductComparison" && product.evaluation && (
              <div className="assistant-product-facts">
                {product.evaluation.map((perspective) => (
                  <details key={perspective.id}>
                    <summary>
                      {perspective.title.replace(" perspective", "")}
                    </summary>
                    <dl>
                      {perspective.rows
                        .filter(
                          (row) =>
                            row.values.some((value) => !value.missing) ||
                            perspective.id === "production"
                        )
                        .map((row, index) => {
                          const value = row.values.find(
                            (value) => value.entityId === product.entityId
                          );
                          return (
                            <div key={index}>
                              <dt>{row.label}</dt>
                              <dd>
                                {!value || value.missing
                                  ? "Not provided"
                                  : `${value.value}${
                                      value.unit ? ` ${value.unit}` : ""
                                    }`}
                                <Evidence sourceId={value?.sourceId} />
                              </dd>
                            </div>
                          );
                        })}
                    </dl>
                    {!perspective.rows.some((row) =>
                      row.values.some((value) => !value.missing)
                    ) && (
                      <p className="assistant-muted">
                        No product facts provided for this perspective.
                      </p>
                    )}
                    {perspective.caveat && (
                      <p className="assistant-muted">{perspective.caveat}</p>
                    )}
                  </details>
                ))}
              </div>
            )}
            {product.matchReasons?.map((reason) => (
              <div key={reason.value}>
                <small>{reason.label}</small>
                <Evidence sourceId={reason.sourceId} />
              </div>
            ))}
            <Evidence sourceId={product.sourceId} />
            <div className="assistant-actions">
              <label>
                <input
                  type="checkbox"
                  checked={selected.includes(product.entityId)}
                  disabled={disabled}
                  onChange={() => onSelect(product)}
                />{" "}
                Select
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  openProductDetail(
                    product.entityId,
                    conversationUrl(conversationId)
                  )
                }
              >
                View details
              </Button>
              <AddToCollections
                entityId={product.entityId}
                name={product.name}
                iconOnly
              />
            </div>
          </div>
        </article>
      ))}
    </div>
  );
  switch (widget.component) {
    case "ProductResultList":
      return (
        <section aria-label="Proposed products">
          <h3>{widget.title ?? "Proposed products"}</h3>
          {widget.brief && <div className="assistant-brief" data-analytics-private>
            <strong>Customer requirements</strong>
            <ul>{widget.brief.mandatory.map((text, i) => <li key={i}>{text}</li>)}</ul>
            {widget.brief.preferences.length > 0 && <p>Preferences: {widget.brief.preferences.join("; ")}</p>}
            {widget.brief.unresolved.length > 0 && <p>Needs confirmation: {widget.brief.unresolved.join("; ")}</p>}
          </div>}
          {productCards(widget.products)}
          {widget.pagination && <nav aria-label="Product result pages" className="assistant-actions">
            <Button variant="outline" size="sm" disabled={disabled || pageBusy || widget.pagination.page === 1} onClick={() => void loadPage(widget.pagination!.resultSetId, widget.pagination!.page - 1)}>Previous</Button>
            <span role="status">{widget.pagination.availableCount === 0 ? 0 : (widget.pagination.page - 1) * 10 + 1}–{Math.min(widget.pagination.page * 10, widget.pagination.availableCount)} of {widget.pagination.availableCount}{widget.pagination.ranked ? " ranked candidates" : " matching products"}{widget.pagination.unverified ? " (unverified)" : ""}</span>
            <Button variant="outline" size="sm" disabled={disabled || pageBusy || widget.pagination.page * 10 >= widget.pagination.availableCount} onClick={() => void loadPage(widget.pagination!.resultSetId, widget.pagination!.page + 1)}>Next</Button>
            {widget.pagination.ranked && <small>Ranked shortlist of up to 200 candidates.</small>}
          </nav>}
          {pageError && <div><p role="alert">{pageError}</p>{widget.pagination && <Button variant="outline" disabled={disabled || pageBusy} onClick={async () => {
            setPageBusy(true);
            try { const refreshed = await api.post(`${conversationPath}/${conversationId}/results/${widget.pagination!.resultSetId}/refresh`, {});
              if (!widgetSchema.safeParse(refreshed).success) throw new Error("Invalid result page.");
              setPageData(refreshed); setPageError("");
            } catch { setPageError("Results could not be refreshed. Your previous results are still available."); }
            finally { setPageBusy(false); }
          }}>Refresh results with the same requirements</Button>}</div>}
        </section>
      );
    case "ProductComparison":
      return (
        <section>
          <h3>{widget.title ?? "Product comparison"}</h3>
          {productCards(widget.products)}
          {widget.perspectives.map((perspective) => (
            <details key={perspective.id} className="assistant-comparison" open>
              <summary>{perspective.title}</summary>
              <div className="assistant-table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Attribute</th>
                      {widget.products.map((product) => (
                        <th key={product.entityId}>{product.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {perspective.rows.map((row, i) => (
                      <tr key={i}>
                        <th>{row.label}</th>
                        {widget.products.map((product) => {
                          const value = row.values.find(
                            (value) => value.entityId === product.entityId
                          );
                          return (
                            <td
                              key={product.entityId}
                              id={`fact-${product.entityId}-${perspective.id}-${encodeURIComponent(row.label)}`}
                              title={value?.sourceId ?? undefined}
                            >
                              {!value || value.missing
                                ? "Not provided"
                                : `${value.value}${
                                    value.unit ? ` ${value.unit}` : ""
                                  }`}
                              <Evidence sourceId={value?.sourceId} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {perspective.recommendation && (
                <p>
                  {perspective.recommendation.label}:{" "}
                  {perspective.recommendation.reason}
                </p>
              )}
              {perspective.caveat && (
                <p className="assistant-muted">{perspective.caveat}</p>
              )}
              {widget.assessment?.conclusions.filter(c => c.perspective === perspective.id).map(conclusion => <div key={conclusion.perspective} className="assistant-conclusion" data-analytics-private>
                <h4>AI comparison conclusion</h4><p>{conclusion.conclusion}</p>
                {evidence(conclusion.evidence)}
                {conclusion.caveats.map((caveat, i) => <p className="assistant-muted" key={i}>{caveat}</p>)}
              </div>)}
            </details>
          ))}
          {widget.assessment ? <section className="assistant-conclusion" data-analytics-private>
            <h3>Recommendation for this customer</h3>
            <p><strong>{widget.assessment.recommendation.kind.replace(/_/g, " ")}</strong> · AI interpretation of recorded facts</p>
            <p>{widget.assessment.recommendation.rationale}</p>
            {widget.assessment.recommendation.productIds.length > 0 && <p><strong>Proposed products: </strong>{widget.products.filter(product => widget.assessment!.recommendation.productIds.includes(product.entityId)).map(product => product.name).join(", ")}</p>}
            {widget.assessment.recommendation.sellingAdvice && <><h4>Selling advice</h4><p>{widget.assessment.recommendation.sellingAdvice}</p></>}
            <h4>When to offer an alternative</h4>
            <p>{widget.assessment.recommendation.alternatives}</p>
            {evidence(widget.assessment.recommendation.evidence)}
            {widget.assessment.recommendation.confirmBeforeProceeding.length > 0 && <><h4>Confirm before proceeding</h4><ul>{widget.assessment.recommendation.confirmBeforeProceeding.map((text, i) => <li key={i}>{text}</li>)}</ul></>}
          </section> : widget.assessmentId && <div><p>The facts are available, but the AI conclusion could not be completed.</p><Button variant="outline" disabled={disabled || pageBusy} onClick={async () => {
            setPageBusy(true); setPageError("");
            try { setPageData(await api.post(`${conversationPath}/${conversationId}/assessments/${widget.assessmentId}/retry`, {})); }
            catch { setPageError("The conclusion could not be completed. Your fact tables are still available."); }
            finally { setPageBusy(false); }
          }}>Retry comparison conclusion</Button>{pageError && <p role="alert">{pageError}</p>}</div>}
        </section>
      );
    case "AttributeHighlights":
      return (
        <section>
          <h3>{widget.title ?? "Highlights"}</h3>
          <dl>
            {widget.attributes.map((fact, i) => (
              <div key={i}>
                <dt>{fact.label}</dt>
                <dd>
                  {fact.value}
                  <Evidence sourceId={fact.sourceId} />
                </dd>
              </div>
            ))}
          </dl>
        </section>
      );
    case "SourceReferences":
      return (
        <details className="assistant-sources">
          <summary>Product sources</summary>
          <dl>
            {widget.sources.map((source, i) => (
              <div key={i}>
                <dt>{source.label}</dt>
                <dd>{source.value}</dd>
              </div>
            ))}
          </dl>
        </details>
      );
    case "ClarificationQuestion":
      return (
        <section>
          <h3>{widget.question}</h3>
          <div className="assistant-actions">
            {widget.options.map((option) => (
              <Button
                key={option}
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => onPrompt(option)}
              >
                {option}
              </Button>
            ))}
          </div>
        </section>
      );
    case "FollowUpSuggestions":
      return (
        <section>
          <h3>You could also ask</h3>
          <div className="assistant-actions">
            {widget.suggestions.map((suggestion) => (
              <Button
                key={suggestion}
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => onPrompt(suggestion)}
              >
                {suggestion}
              </Button>
            ))}
          </div>
        </section>
      );
    case "ClassicSearchFallback":
      return (
        <section>
          <p>{widget.message}</p>
          <Button
            variant="outline"
            onClick={() => {
              history.pushState(
                null,
                "",
                `/product-management/search?q=${encodeURIComponent(
                  widget.query
                )}&page=1&pageSize=20`
              );
              window.dispatchEvent(new Event(PRODUCT_NAVIGATION_EVENT));
            }}
          >
            Open product search
          </Button>
        </section>
      );
  }
}
